import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Info, Pencil, Trash2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { ShiftStatusBadge, DeleteShiftModal } from "../components";
import { fetchShiftById, checkShiftAssignment, updateShiftOnServer } from "../../../services/shiftService";
import { isOvernightShift, computeGrossDurationHours, toHHMM } from "../utils/shiftTimeUtils";
import { TOAST_KEY } from "./ShiftsListPage";

const sectionClass =
"bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden p-4 sm:p-6 mb-5";
const fieldLabelClass = "block text-xs font-semibold per-file-hint mb-1.5";
const fieldValueClass = "text-sm text-foreground";

function Field({ label, value }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className={fieldLabelClass}>{label}</span>
      <span className={fieldValueClass}>{value}</span>
    </div>);

}

function yesNo(value) {
  return value ? "Yes" : "No";
}

export function ShiftDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [deletingShift, setDeletingShift] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchShiftById(id)
    .then((data) => setShift(data))
    .catch(() => setLoadError("Failed to load shift."))
    .finally(() => setLoading(false));
  }, [id]);

  const handleDeleteClick = async () => {
    if (!shift) return;
    try {
      const { assignedEmployeeCount } = await checkShiftAssignment(shift.id);
      setDeletingShift({ ...shift, assignedEmployeeCount });
    } catch {
      setDeletingShift({ ...shift, assignedEmployeeCount: 0 });
    }
  };

  const executeDeletion = async () => {
    if (!deletingShift) return;
    setDeleting(true);
    try {
      await updateShiftOnServer({ ...deletingShift, isActive: false });
      sessionStorage.setItem(
        TOAST_KEY,
        JSON.stringify({ message: `Shift "${deletingShift.shiftName}" deleted.`, type: "success" })
      );
      navigate("/hrms-team/config/shifts");
    } catch (err) {
      setDeletingShift(null);
      setLoadError(err?.message || "Failed to delete shift.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout formLayout="standard">
          <PageTitleRow className="mb-5" back={<PageBackLink to="/hrms-team/config/shifts" />}><h1><Trans i18nKey="organizationShifts.shiftDetails">Shift details</Trans></h1></PageTitleRow>
        <div className="py-12 text-center text-sm per-file-hint"><Trans i18nKey="organizationShifts.loadingShift" /></div>
      </AppLayout>);

  }

  if (loadError || !shift) {
    return (
      <AppLayout formLayout="standard">
          <PageTitleRow className="mb-5" back={<PageBackLink to="/hrms-team/config/shifts" />}><h1><Trans i18nKey="organizationShifts.shiftDetails">Shift details</Trans></h1></PageTitleRow>
        <div className="py-12 text-center text-sm text-error">
          {loadError || "Shift not found."}
        </div>
      </AppLayout>);

  }

  const startHHMM = toHHMM(shift.shiftStartTime);
  const endHHMM = toHHMM(shift.shiftEndTime);
  const overnight = isOvernightShift(startHHMM, endHHMM);
  const grossDuration = computeGrossDurationHours(startHHMM, endHHMM);
  const otEnabled = !!shift.overtimeEnabled;

  return (
    <AppLayout formLayout="standard">
       
      <div className="mb-5 w-full">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <PageTitleRow back={<PageBackLink to="/hrms-team/config/shifts" />}>
              <h1 className="text-[28px] leading-tight font-bold text-foreground">{shift.shiftName}</h1>
            </PageTitleRow>
            <div className="mt-2">
              <ShiftStatusBadge status={shift.isActive ? "Active" : "Inactive"} />
            </div>
          </div>
          <div className="flex gap-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/hrms-team/config/shifts/edit/${shift.id}`)}
              className="h-10 px-4 rounded-xl border-(--color-text-border) text-foreground hover:bg-workspace/40">
              
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> <Trans i18nKey="common.edit" />
            </Button>
            <Button
              type="button"
              onClick={handleDeleteClick}
              className="h-10 px-4 rounded-xl bg-error text-white hover:bg-error/90">
              
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> <Trans i18nKey="common.delete" />
            </Button>
          </div>
        </div>
      </div>

      <div className="w-full">
        {/* 01 — Basic Details */}
        <div className={sectionClass}>
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="organizationShifts.01BasicDetails" /></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Shift Name" value={shift.shiftName} />
            <Field label="Is Active" value={yesNo(shift.isActive)} />
            <Field label="Shift Start Time" value={startHHMM || "—"} />
            <Field label="Shift End Time" value={endHHMM || "—"} />
          </div>
          {overnight &&
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-accent/25 bg-accent/5 px-3.5 py-2.5 text-[13px] text-foreground">
              <Info className="h-4 w-4 shrink-0 mt-0.5 text-accent" />
              <span>
                <Trans i18nKey="dateTime.thisShiftEndsTheNextDayGrossDuration" />
                {grossDuration !== null ? ` = ${grossDuration}h.` : "."}
              </span>
            </div>
          }
          {!overnight && grossDuration !== null &&
          <div className="mt-4 text-[12px] per-file-hint">
              <Trans i18nKey="dateTime.grossShiftDuration" /> <span className="font-semibold text-foreground">{grossDuration}<Trans i18nKey="uom.h" /></span>
            </div>
          }
        </div>

        {/* 02 — Attendance Thresholds */}
        <div className={sectionClass}>
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="organizationShifts.02AttendanceThresholds" /></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Strict Working Hours" value={shift.strictWorkingHours ?? "—"} />
            <Field label="Half Day Working Hours" value={shift.halfDayWorkingHours ?? "—"} />
            <Field label="Full Day Working Hours" value={shift.fullDayWorkingHours ?? "—"} />
            <Field label="Late Arrival Buffer Minutes" value={shift.lateArrivalBufferMinutes ?? "—"} />
            <Field label="Early Exit Buffer Minutes" value={shift.earlyExitBufferMinutes ?? "—"} />
          </div>
        </div>

        {/* 03 — Overtime & Comp Off */}
        <div className={sectionClass}>
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="dateTime.03OvertimeCompOff" /></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Overtime Enabled" value={yesNo(shift.overtimeEnabled)} />
            <Field label="Comp Off Enabled" value={yesNo(shift.compOffEnabled)} />
            <Field label="Minimum Overtime Hours" value={otEnabled ? shift.minimumOvertimeHours ?? "—" : "Not applicable"} />
            <Field label="Half Day Overtime Hours" value={otEnabled ? shift.halfDayOvertimeHours ?? "—" : "Not applicable"} />
            <Field label="Full Day Overtime Hours" value={otEnabled ? shift.fullDayOvertimeHours ?? "—" : "Not applicable"} />
          </div>
        </div>

        {/* 04 — Attendance Controls */}
        <div className={sectionClass}>
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="organizationShifts.04AttendanceControls" /></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="HR Can Mark Attendance" value={yesNo(shift.hrCanMarkAttendance)} />
            <Field label="HR Can Amend Time" value={yesNo(shift.hrCanAmendTime)} />
            <Field label="Mandatory Report Before Checkout" value={yesNo(shift.mandatoryReportBeforeCheckout)} />
            <Field label="Regularization Approval Flow" value={shift.regularizationApprovalFlow ?? "—"} />
          </div>
        </div>
      </div>

      <DeleteShiftModal
        deletingShift={deletingShift}
        setDeletingShift={setDeletingShift}
        executeDeletion={executeDeletion}
        deleting={deleting} />
      
    </AppLayout>);

}
